import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, Clock, ChevronRight, ChevronLeft, Zap, ShieldCheck, Handshake, X, ZoomIn, ZoomOut, Maximize2, Ticket } from "lucide-react";
import DayPassDialog from "@/components/PassDialog";
import SponsorDialog from "@/components/SponsorDialog";
import ActivitiesDialog from "@/components/ActivitiesDialog";
import SuccessView from "@/components/SuccessView";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

export default function Landing() {
  const [content, setContent] = useState(null);
  const [dayOpen, setDayOpen] = useState(false);
  const [merakiOpen, setMerakiOpen] = useState(false);
  const [sponsorOpen, setSponsorOpen] = useState(false);
  const [activitiesOpen, setActivitiesOpen] = useState(false);
  const [paySuccess, setPaySuccess] = useState(null); // { label, amount }

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  if (!content) {
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-[#A1A1AA]">Loading…</div>;
  }

  const ev = content.event || {};
  const dayDisabled = !!content.day_pass?.disabled;
  const merakiDisabled = !!content.meraki_pass?.disabled;
  const dayMsg = content.day_pass?.sold_out_message || "Day Pass is currently unavailable.";
  const merakiMsg = content.meraki_pass?.sold_out_message || "Meraki Pass is currently unavailable.";

  const openDay = () => { if (dayDisabled) { toast.error(dayMsg); return; } setDayOpen(true); };
  const openMeraki = () => { if (merakiDisabled) { toast.error(merakiMsg); return; } setMerakiOpen(true); };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative">
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-40 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-display text-xl font-900 uppercase tracking-tight" data-testid="site-logo">
            Meraki Moto <span className="text-[#FF4500]">Mania</span>
          </span>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[72vh] sm:min-h-[92vh] flex items-end overflow-hidden bg-black pt-16 sm:pt-0">
        <img
          src={mediaUrl(content.hero_image)}
          alt="Meraki Moto Mania"
          data-testid="hero-image"
          className="absolute top-16 sm:top-0 inset-x-0 bottom-0 h-[calc(100%-4rem)] sm:h-full w-full object-contain object-top sm:object-cover sm:object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-black/40" />
        <div className="absolute inset-0 noise-overlay opacity-[0.06] pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-16 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#FF4500] font-semibold mb-4">
              <Zap className="h-4 w-4" /> {ev.tagline}
            </span>
            <h1
              data-testid="event-name"
              className="font-display font-900 uppercase leading-[0.88] tracking-tight text-6xl sm:text-7xl lg:text-8xl max-w-4xl"
            >
              {ev.name}
            </h1>
            <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3 text-sm text-[#D4D4D8]">
              <span className="flex items-center gap-2" data-testid="event-date"><Calendar className="h-4 w-4 text-[#FF4500]" /> {ev.date}</span>
              <span className="flex items-center gap-2" data-testid="event-time"><Clock className="h-4 w-4 text-[#FF4500]" /> {ev.time}</span>
              <span className="flex items-center gap-2" data-testid="event-venue"><MapPin className="h-4 w-4 text-[#FF4500]" /> {ev.venue}</span>
            </div>

            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Button
                data-testid="open-day-pass-button"
                onClick={openDay}
                aria-disabled={dayDisabled}
                title={dayDisabled ? dayMsg : ""}
                className={`group h-14 px-8 rounded-full text-white text-base font-bold uppercase tracking-wide transition-colors ${dayDisabled ? "bg-[#3f3f46] hover:bg-[#3f3f46] cursor-not-allowed opacity-70" : "bg-[#FF4500] hover:bg-[#FF5722]"}`}
              >
                {dayDisabled ? "Day Pass · Sold Out" : (content.buttons?.day_pass_label || "Day Pass")}
                {!dayDisabled && <ChevronRight className="h-5 w-5 ml-1 group-hover:translate-x-1 transition-transform" />}
              </Button>
              <Button
                data-testid="open-meraki-pass-button"
                onClick={openMeraki}
                variant="outline"
                aria-disabled={merakiDisabled}
                title={merakiDisabled ? merakiMsg : ""}
                className={`group h-14 px-8 rounded-full border-white/25 text-white text-base font-bold uppercase tracking-wide transition-colors ${merakiDisabled ? "bg-white/[0.02] cursor-not-allowed opacity-60" : "bg-white/5 hover:bg-white/10"}`}
              >
                {merakiDisabled ? "Meraki Pass · Sold Out" : (content.buttons?.meraki_pass_label || "Meraki Pass")}
                {!merakiDisabled && <ChevronRight className="h-5 w-5 ml-1 group-hover:translate-x-1 transition-transform" />}
              </Button>
              <Button
                data-testid="open-activities-button"
                onClick={() => setActivitiesOpen(true)}
                variant="outline"
                className="group h-14 px-8 rounded-full border-white/25 text-white text-base font-bold uppercase tracking-wide bg-white/5 hover:bg-white/10 transition-colors"
              >
                <Ticket className="h-5 w-5 mr-2 text-[#FF4500]" />
                Activities
                <ChevronRight className="h-5 w-5 ml-1 group-hover:translate-x-1 transition-transform" />
              </Button>
              {content.buttons?.sponsor_enabled !== false && (
                <Button
                  data-testid="open-sponsor-button"
                  onClick={() => setSponsorOpen(true)}
                  variant="ghost"
                  className="group h-14 px-8 rounded-full border border-[#FF4500]/40 text-[#FF7043] hover:bg-[#FF4500]/10 hover:text-[#FF7043] text-base font-bold uppercase tracking-wide transition-colors"
                >
                  <Handshake className="h-5 w-5 mr-2" />
                  {content.buttons?.sponsor_label || "Be Our Sponsor"}
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Logo Marquee */}
      <LogoMarquee logos={content.marquee_logos} />

      {/* Riding Community Partners */}
      <CommunityPartners partners={content.community_partners} />

      {/* Event Details */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <span className="text-xs uppercase tracking-[0.25em] text-[#FF4500] font-semibold">The Event</span>
            <h2 className="font-display text-4xl sm:text-5xl font-800 uppercase tracking-tight mt-3 mb-6">
              About Meraki Moto Mania
            </h2>
            <p data-testid="event-description" className="text-[#A1A1AA] text-lg leading-relaxed max-w-2xl">
              {ev.description}
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="rounded-2xl border border-border bg-[#121212] p-8">
              <h3 className="font-display text-2xl font-700 uppercase tracking-tight mb-5 flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-[#FF4500]" /> Highlights
              </h3>
              <ul className="space-y-3" data-testid="event-highlights">
                {ev.highlights?.map((h, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#E4E4E7]">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#FF4500] shrink-0" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Event Details Gallery */}
      <EventGallery images={content.event_images} />

      {/* CTA band */}
      <section className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-6">
          <PassCard
            title={content.buttons?.day_pass_label || "Day Pass"}
            desc="Single-day entry to the arena, track sessions and expo."
            onClick={openDay}
            disabled={dayDisabled}
            testid="cta-day-pass-card"
          />
          <PassCard
            title={content.buttons?.meraki_pass_label || "Meraki Pass"}
            desc="The full weekend — stay, meals, merch and every experience."
            highlight
            onClick={openMeraki}
            disabled={merakiDisabled}
            testid="cta-meraki-pass-card"
          />
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-8 text-sm text-[#71717A] flex flex-col sm:flex-row justify-between gap-2">
          <span>© 2026 {ev.name}. All rights reserved.</span>
          <span>Ride safe. Ride proud.</span>
        </div>
      </footer>

      <DayPassDialog open={dayOpen} onOpenChange={setDayOpen} content={content} passKey="day" onPaid={(label, amount, name) => setPaySuccess({ label, amount, name })} />
      <DayPassDialog open={merakiOpen} onOpenChange={setMerakiOpen} content={content} passKey="meraki" onPaid={(label, amount, name) => setPaySuccess({ label, amount, name })} />
      <SponsorDialog open={sponsorOpen} onOpenChange={setSponsorOpen} />
      <ActivitiesDialog open={activitiesOpen} onOpenChange={setActivitiesOpen} onPaid={(label, amount, name) => setPaySuccess({ label, amount, name })} />

      <Dialog open={!!paySuccess} onOpenChange={(o) => !o && setPaySuccess(null)}>
        <DialogContent data-testid="payment-success-dialog" className="bg-[#121212] border-border max-w-md">
          <DialogTitle className="sr-only">Payment Successful</DialogTitle>
          {paySuccess && (
            <SuccessView passLabel={paySuccess.label} amount={paySuccess.amount} leadName={paySuccess.name} onClose={() => setPaySuccess(null)} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PassCard({ title, desc, onClick, highlight, testid, disabled }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-left rounded-2xl border p-8 transition-colors group ${disabled ? "border-border bg-[#0F0F0F] opacity-70 cursor-not-allowed" : highlight ? "border-[#FF4500]/50 bg-[#FF4500]/[0.06] hover:bg-[#FF4500]/[0.1]" : "border-border bg-[#121212] hover:border-[#3f3f46]"}`}
    >
      <h3 className="font-display text-3xl font-800 uppercase tracking-tight">{title}</h3>
      <p className="mt-3 text-[#A1A1AA]">{desc}</p>
      {disabled ? (
        <span className="mt-6 inline-flex items-center gap-1 text-sm font-semibold uppercase tracking-wide text-[#71717A]">
          Sold Out
        </span>
      ) : (
        <span className="mt-6 inline-flex items-center gap-1 text-sm font-semibold uppercase tracking-wide text-[#FF4500]">
          Register now <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </span>
      )}
    </button>
  );
}

function LogoMarquee({ logos }) {
  const list = (logos || []).filter(Boolean);
  if (list.length === 0) return null;
  const loop = [...list, ...list];
  return (
    <section className="border-t border-border bg-[#0A0A0A]" data-testid="logo-marquee">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-8">
          <span className="text-xs uppercase tracking-[0.25em] text-[#FF4500] font-semibold">Our Partners</span>
        </div>
        <div className="marquee-mask overflow-hidden">
          <div className="flex w-max items-center gap-16 animate-marquee hover:[animation-play-state:paused]" data-testid="logo-marquee-track">
            {loop.map((logo, idx) => (
              <div key={idx} className="shrink-0 flex items-center justify-center h-32 w-72" data-testid={`logo-marquee-item-${idx}`}>
                <img
                  src={mediaUrl(logo)}
                  alt={`Partner ${(idx % list.length) + 1}`}
                  className="max-h-32 max-w-72 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CommunityPartners({ partners }) {
  const list = (partners || []).filter((p) => p && p.logo);
  if (list.length === 0) return null;
  const loop = [...list, ...list];
  return (
    <section className="border-t border-border bg-[#0A0A0A]" data-testid="community-partners">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <span className="text-xs uppercase tracking-[0.25em] text-[#FF4500] font-semibold">Riding Community Partners</span>
        </div>
        <div className="marquee-mask overflow-hidden">
          <div className="flex w-max items-start gap-16 animate-marquee hover:[animation-play-state:paused]" data-testid="community-partners-track">
            {loop.map((p, idx) => (
              <div
                key={idx}
                data-testid={`community-partner-${idx}`}
                className="group shrink-0 w-48 flex flex-col items-center text-center gap-4"
              >
                <div className="h-28 w-full flex items-center justify-center">
                  <img
                    src={mediaUrl(p.logo)}
                    alt={p.name || `Community partner ${(idx % list.length) + 1}`}
                    className="max-h-28 max-w-full w-auto object-contain opacity-90 group-hover:opacity-100 transition-opacity"
                  />
                </div>
                {p.name && (
                  <span className="text-sm font-semibold text-[#E4E4E7] leading-tight" data-testid={`community-partner-${idx}-name`}>
                    {p.name}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function EventGallery({ images }) {
  const list = (images || []).filter(Boolean);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (list.length < 2 || paused || lightbox) return;
    const t = setInterval(() => setI((p) => (p + 1) % list.length), 5000);
    return () => clearInterval(t);
  }, [list.length, paused, lightbox]);

  const go = (n) => { setI((n + list.length) % list.length); setZoom(1); };

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      else if (e.key === "ArrowLeft") go(i - 1);
      else if (e.key === "ArrowRight") go(i + 1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox, i, list.length]);

  if (list.length === 0) return null;

  const openLightbox = () => { setZoom(1); setLightbox(true); };

  return (
    <section className="border-t border-border" data-testid="event-gallery">
      <div className="max-w-5xl mx-auto px-6 py-24">
        <div className="text-center mb-10">
          <span className="text-xs uppercase tracking-[0.25em] text-[#FF4500] font-semibold">Event Details</span>
          <h2 className="font-display text-4xl sm:text-5xl font-800 uppercase tracking-tight mt-3">The Full Lineup</h2>
        </div>
        <div
          className="relative rounded-2xl overflow-hidden border border-border bg-black group"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="event-gallery-stage"
        >
          <div className="flex items-center justify-center min-h-[300px] max-h-[80vh]">
            <motion.img
              key={i}
              src={mediaUrl(list[i])}
              alt={`Event detail ${i + 1}`}
              data-testid="event-gallery-image"
              onClick={openLightbox}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="w-full max-h-[80vh] object-contain cursor-zoom-in"
            />
          </div>
          {/* Expand hint */}
          <button
            onClick={openLightbox}
            data-testid="gallery-expand"
            className="absolute top-3 right-3 h-10 w-10 rounded-full bg-black/60 border border-white/15 backdrop-blur flex items-center justify-center text-white opacity-0 group-hover:opacity-100 hover:bg-[#FF4500] transition-all"
            title="View full size"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
          {list.length > 1 && (
            <>
              <button data-testid="gallery-prev" onClick={() => go(i - 1)} className="absolute left-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/60 border border-white/15 backdrop-blur flex items-center justify-center text-white hover:bg-[#FF4500] transition-colors">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button data-testid="gallery-next" onClick={() => go(i + 1)} className="absolute right-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/60 border border-white/15 backdrop-blur flex items-center justify-center text-white hover:bg-[#FF4500] transition-colors">
                <ChevronRight className="h-5 w-5" />
              </button>
              <div className="absolute bottom-4 inset-x-0 flex items-center justify-center gap-2">
                {list.map((_, idx) => (
                  <button key={idx} data-testid={`gallery-dot-${idx}`} onClick={() => go(idx)} className={`h-2 rounded-full transition-all ${idx === i ? "w-7 bg-[#FF4500]" : "w-2 bg-white/40 hover:bg-white/70"}`} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-sm flex items-center justify-center animate-fade-up"
          onClick={() => setLightbox(false)}
          data-testid="gallery-lightbox"
        >
          <div className="absolute top-4 right-4 flex items-center gap-2 z-10" onClick={(e) => e.stopPropagation()}>
            <button data-testid="lightbox-zoom-out" onClick={() => setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)))} className="h-11 w-11 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-white hover:bg-white/20 transition-colors disabled:opacity-40" disabled={zoom <= 1}>
              <ZoomOut className="h-5 w-5" />
            </button>
            <span data-testid="lightbox-zoom-level" className="text-xs text-white/70 w-12 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
            <button data-testid="lightbox-zoom-in" onClick={() => setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))} className="h-11 w-11 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-white hover:bg-white/20 transition-colors disabled:opacity-40" disabled={zoom >= 3}>
              <ZoomIn className="h-5 w-5" />
            </button>
            <button data-testid="lightbox-close" onClick={() => setLightbox(false)} className="h-11 w-11 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-white hover:bg-[#FF4500] transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="relative max-w-[92vw] max-h-[88vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <img
              src={mediaUrl(list[i])}
              alt={`Event detail ${i + 1} full`}
              data-testid="lightbox-image"
              style={{ transform: `scale(${zoom})`, transformOrigin: "center" }}
              className="max-w-[92vw] max-h-[88vh] object-contain transition-transform duration-200 select-none"
            />
          </div>

          {list.length > 1 && (
            <>
              <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); go(i - 1); }} className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-white hover:bg-[#FF4500] transition-colors">
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); go(i + 1); }} className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-white hover:bg-[#FF4500] transition-colors">
                <ChevronRight className="h-6 w-6" />
              </button>
              <div className="absolute bottom-5 inset-x-0 text-center text-sm text-white/60" data-testid="lightbox-counter">
                {i + 1} / {list.length}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
